import { type ImgHTMLAttributes, useState } from "react";

const imageWidths = [96, 192, 320, 480, 640, 960, 1280, 1920];
const publicImageExtension = /\.(jpe?g|png|webp|avif)$/i;
const mediaHosts = new Set([
  "media.lockerverse.com",
  "dev-media.lockerverse.com",
]);

function publicImageUrl(src: string | undefined): URL | undefined {
  if (!src) {
    return;
  }
  try {
    const url = new URL(src);
    if (
      url.protocol !== "https:" ||
      !mediaHosts.has(url.hostname) ||
      url.username ||
      url.password ||
      url.port ||
      url.search ||
      url.hash ||
      url.pathname.startsWith("/__image/") ||
      url.pathname.includes("/chat-media/") ||
      !publicImageExtension.test(url.pathname)
    ) {
      return;
    }
    return url;
  } catch {
    // Relative, local, and malformed sources keep their original URL.
  }
}

function resizedUrl(url: URL, width: number): string {
  url.searchParams.set("w", String(width));
  url.searchParams.set("f", "webp");
  return url.href;
}

/** Keep source identities in the auction data; resize only displayed photos. */
export function AuctionImage({
  alt,
  width,
  height,
  src,
  maxWidth,
  onError,
  decoding = "async",
  ...props
}: Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet" | "alt" | "width" | "height"
> & {
  alt: string;
  width: number;
  height: number;
  src: string | undefined;
  maxWidth: number;
}) {
  const [failedSource, setFailedSource] = useState<string>();
  const url = failedSource === src ? undefined : publicImageUrl(src);
  const widths = imageWidths.filter((profileWidth) => profileWidth <= maxWidth);
  const largestWidth = widths.at(-1) ?? 96;
  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: A load error retries the original image; it is not a user interaction.
    <img
      {...props}
      alt={alt}
      decoding={decoding}
      height={height}
      onError={(event) => {
        if (url) {
          setFailedSource(src);
        } else {
          onError?.(event);
        }
      }}
      src={url ? resizedUrl(url, largestWidth) : src}
      srcSet={
        url
          ? widths
              .map(
                (profileWidth) =>
                  `${resizedUrl(url, profileWidth)} ${profileWidth}w`
              )
              .join(", ")
          : undefined
      }
      width={width}
    />
  );
}
